import { CalendarDays, Link2, MessageSquare, Paperclip } from "lucide-react";

import { formatDate } from "../../lib/date";
import { AvatarGroup } from "../Avatar";
import { cn } from "../cn";
import { PriorityBadge, StatusTag, Tag } from "../Tag";
import { TaskTypeIcon } from "../TaskTypeIcon";
import type { KanbanCardData } from "@taskflow/client-core/boards";

interface TaskCardProps {
  card: KanbanCardData;
  onOpen?: (key: string) => void;
  dragging?: boolean;
}

/** Красная полоса слева — только у задач с высоким приоритетом. */
function isHighPriority(name: string | null | undefined): boolean {
  return name?.trim().toLowerCase() === "высокий";
}

export function TaskCard({ card, onOpen, dragging = false }: TaskCardProps) {
  return (
    <article
      // Открывается кликом по любому месту карточки, а не только по названию:
      // края, теги и срок тоже часть карточки. Кнопка внутри остаётся для
      // клавиатуры и скринридера — её клик всплывает сюда же.
      onClick={onOpen ? () => onOpen(card.key) : undefined}
      // Цвет отметки о просмотре смешивается с поверхностью темы: в тёмной
      // теме чистый пастельный фон сделал бы светлый текст нечитаемым.
      style={
        card.highlightColor
          ? {
              backgroundColor: `color-mix(in srgb, ${card.highlightColor} var(--view-mark-strength), var(--color-surface))`,
            }
          : undefined
      }
      className={cn(
        "flex flex-col gap-2 rounded-lg border bg-surface p-2.5 shadow-sm transition-shadow",
        onOpen && "cursor-pointer",
        dragging ? "rotate-1 border-accent shadow-lg" : "border-line hover:shadow-md",
        // Полоса дублирует бейдж приоритета: одного цвета мало (§19.4).
        isHighPriority(card.priorityName) && "border-l-2 border-l-danger",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <button type="button" className="min-w-0 flex-1 cursor-[inherit] text-left">
          {/* Значок стоит рядом с номером, а не в нижней строке: тип —
              это про то, ЧТО за задача, и читается вместе с её номером,
              прежде чем человек доберётся до срока и исполнителей. */}
          <span className="flex items-center gap-1">
            <TaskTypeIcon iconKey={card.typeIconKey} name={card.typeName} />
            <span className="font-mono text-[11px] font-semibold text-faint">{card.key}</span>
          </span>
          <p className="mt-0.5 line-clamp-3 text-[13px] leading-snug font-semibold text-fg">
            {card.title}
          </p>
        </button>
      </div>

      {card.statusTag && <StatusTag label={card.statusTag.label} code={card.statusTag.code} />}

      {card.tags && card.tags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {card.tags.map((tag) => (
            <Tag key={tag.id} size="sm" color={tag.color}>
              {tag.name}
            </Tag>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-2.5">
          {card.priorityName !== null && card.priorityName !== undefined && (
            <PriorityBadge name={card.priorityName} color={card.priorityColor} />
          )}
          {card.dueDate !== null && card.dueDate !== undefined && (
            <span
              className={cn(
                "inline-flex items-center gap-1 text-[11px] font-semibold tabular-nums",
                card.overdue === true ? "text-danger" : "text-faint",
              )}
              title={card.overdue === true ? "Срок истёк" : "Срок"}
            >
              <CalendarDays className="size-3" aria-hidden />
              {formatDate(card.dueDate)}
            </span>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {typeof card.commentCount === "number" && card.commentCount > 0 && (
            <span className="inline-flex items-center gap-0.5 text-[11px] text-faint tabular-nums">
              <MessageSquare className="size-3" aria-hidden />
              {card.commentCount}
            </span>
          )}
          {typeof card.linkCount === "number" && card.linkCount > 0 && (
            <span
              className="inline-flex items-center gap-0.5 text-[11px] text-faint tabular-nums"
              title="Связанные задачи"
              aria-label={`Связанных задач: ${card.linkCount}`}
            >
              <Link2 className="size-3" aria-hidden />
              {card.linkCount}
            </span>
          )}
          {typeof card.attachmentCount === "number" && card.attachmentCount > 0 && (
            <span className="inline-flex items-center gap-0.5 text-[11px] text-faint tabular-nums">
              <Paperclip className="size-3" aria-hidden />
              {card.attachmentCount}
            </span>
          )}
          {card.assignees.length > 0 && <AvatarGroup people={card.assignees} size="xs" max={3} />}
        </div>
      </div>
    </article>
  );
}
